import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Blueprint, ModuleField } from "../types";
import { findModule, moduleContext, pathSegment, ROUTER } from "./blueprint";
import { ModuleCatalog } from "../useModuleCatalog";
import { MappingContext } from "./MappingInput";
import { moduleColor } from "../moduleStyle";

interface DataPickerProps {
    blueprint: Blueprint;
    moduleId: number;
    catalog: ModuleCatalog;
    /** First output bundle of each module in the last run, as sample data. */
    samples: Record<number, any>;
}

interface Item {
    path: string;
    label: string;
    sample?: any;
}

const MAX_DEPTH = 3;

const fromFields = (fields: ModuleField[], prefix = ''): Item[] =>
    fields.flatMap((f) => {
        const path = prefix + pathSegment(f.name);
        const own: Item = { path, label: f.label || f.name };
        const nested = f.spec && f.type === 'collection' ? fromFields(f.spec, path + '.') : [];

        return [own, ...nested];
    });

const fromSample = (value: any, prefix = '', depth = 0): Item[] => {
    if (!value || typeof value !== 'object' || depth >= MAX_DEPTH) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.length > 0 && typeof value[0] === 'object' ? fromSample(value[0], prefix.replace(/\.$/, '') + '[1].', depth + 1) : [];
    }

    return Object.entries(value).flatMap(([key, v]) => {
        const path = prefix + pathSegment(key);

        return [{ path, label: key, sample: v }, ...fromSample(v, path + '.', depth + 1)];
    });
};

const preview = (v: any): string => {
    if (v === null || v === undefined) {
        return '';
    }
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);

    return s.length > 40 ? s.slice(0, 37) + '…' : s;
};

/**
 * What a mapping field can use: the outputs of the modules that run before
 * this one (from their schema, and from the last run's data when there is one),
 * plus the mapping functions.
 */
const DataPicker = ({ blueprint, moduleId, catalog, samples }: DataPickerProps) => {
    const { t } = useTranslation(['workflow']);
    const { insert } = useContext(MappingContext);
    const [tab, setTab] = useState<'data' | 'functions'>('data');
    const [query, setQuery] = useState('');
    const [hint, setHint] = useState(false);

    const groups = useMemo(() => {
        const context = moduleContext(blueprint, moduleId);
        const ids = (context?.ancestors || []).filter((id) => {
            const key = findModule(blueprint, id)?.module || '';

            return key !== ROUTER && catalog.byKey[key]?.kind !== 'directive';
        });

        return ids.map((id) => {
            const key = findModule(blueprint, id)?.module || '';
            const definition = catalog.byKey[key];
            const sampleItems = fromSample(samples[id]);
            const schemaItems = fromFields(definition?.output || []);
            const seen = new Set(sampleItems.map((i) => i.path));
            const items = [...sampleItems, ...schemaItems.filter((i) => !seen.has(i.path))];

            return { id, key, label: definition?.label || key, items };
        });
    }, [blueprint, moduleId, catalog, samples]);

    const put = (text: string) => {
        if (!insert(text)) {
            setHint(true);
        }
    };

    const q = query.trim().toLowerCase();

    return (
        <div className="border rounded bg-light">
            <div className="d-flex align-items-center gap-2 p-3 border-bottom">
                <button type="button" className={"btn btn-sm " + (tab === 'data' ? 'btn-primary' : 'btn-light')} onClick={() => setTab('data')}>{t('workflow:pickerData')}</button>
                <button type="button" className={"btn btn-sm " + (tab === 'functions' ? 'btn-primary' : 'btn-light')} onClick={() => setTab('functions')}>{t('workflow:pickerFunctions')}</button>
                <div className="search-box flex-grow-1 ms-1">
                    <input className="form-control search" placeholder={t('workflow:search')} value={query} onChange={(e) => setQuery(e.target.value)} />
                    <i className="ri-search-line search-icon" />
                </div>
            </div>
            {hint && <div className="fs-13 text-warning px-3 pt-3">{t('workflow:pickerFocusHint')}</div>}
            <div className="p-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
                {tab === 'data' && (groups.length === 0
                    ? <div className="fs-13 text-muted">{t('workflow:pickerNoData')}</div>
                    : groups.map((group) => {
                        const items = group.items.filter((i) => !q || i.path.toLowerCase().includes(q) || i.label.toLowerCase().includes(q));

                        return (
                            <div key={group.id} className="mb-3">
                                <div className="fs-13 fw-semibold mb-2">
                                    <span className="badge rounded-pill me-1" style={{ background: moduleColor(group.key) }}>{group.id}</span>{group.label}
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                    {items.length === 0 && <span className="fs-13 text-muted">{t('workflow:pickerRunForData')}</span>}
                                    {items.map((item) => (
                                        <button key={item.path} type="button" className="btn btn-sm btn-soft-primary py-0" title={preview(item.sample)} onClick={() => put(`{{${group.id}.${item.path}}}`)}>
                                            {item.path}{item.sample !== undefined && typeof item.sample !== 'object' && <span className="text-muted ms-1">{preview(item.sample)}</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    }))}
                {tab === 'functions' &&
                    <div className="d-flex flex-wrap gap-1">
                        {catalog.functions.filter((f) => !q || f.toLowerCase().includes(q)).map((name) => (
                            <button key={name} type="button" className="btn btn-sm btn-soft-secondary py-0 font-monospace" onClick={() => put(`{{${name}()}}`)}>{name}</button>
                        ))}
                        {['now', 'emptystring', 'newline', 'true', 'false', 'null'].filter((k) => !q || k.includes(q)).map((k) => (
                            <button key={k} type="button" className="btn btn-sm btn-soft-info py-0 font-monospace" onClick={() => put(`{{${k}}}`)}>{k}</button>
                        ))}
                    </div>
                }
            </div>
        </div>
    );
};

export default DataPicker;
